import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import axios from 'axios';
import { Compass, MapPin, RefreshCw, Navigation, AlertCircle, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Qibla({ user }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const compassRef = useRef(null);
  
  const [qiblaDirection, setQiblaDirection] = useState(null);
  const [deviceHeading, setDeviceHeading] = useState(0);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [isCalibrated, setIsCalibrated] = useState(false);

  // Fetch Qibla direction from Aladhan API
  const fetchQiblaDirection = async (lat, lon) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `https://api.aladhan.com/v1/qibla/${lat}/${lon}`
      );
      
      if (response.data.code === 200) {
        setQiblaDirection(response.data.data.direction);
      } else {
        throw new Error('Failed to get Qibla direction');
      }
    } catch (err) {
      console.error('Error fetching Qibla:', err);
      setError(lang === 'hr' 
        ? 'Greška pri dohvaćanju smjera Kible' 
        : 'Error fetching Qibla direction');
    } finally {
      setLoading(false);
    }
  };

  // Get user's location
  const getLocation = () => {
    setLoading(true);
    setError(null);
    
    if (!navigator.geolocation) {
      setError(lang === 'hr' 
        ? 'Geolokacija nije podržana u vašem pregledniku' 
        : 'Geolocation is not supported by your browser');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
        fetchQiblaDirection(latitude, longitude);
      },
      (err) => {
        console.error('Geolocation error:', err);
        setError(lang === 'hr' 
          ? 'Molimo omogućite pristup lokaciji' 
          : 'Please enable location access');
        setLoading(false);
      },
      { enableHighAccuracy: true }
    );
  };

  // Initialize compass with device orientation
  const initCompass = async () => {
    // Check if DeviceOrientationEvent is available
    if (!window.DeviceOrientationEvent) {
      setError(lang === 'hr' 
        ? 'Kompas nije podržan na ovom uređaju' 
        : 'Compass not supported on this device');
      return;
    }

    // For iOS 13+ devices, we need to request permission
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      try {
        const permission = await DeviceOrientationEvent.requestPermission();
        if (permission === 'granted') {
          setPermissionGranted(true);
          startCompass();
        } else {
          setError(lang === 'hr' 
            ? 'Pristup kompasu odbijen' 
            : 'Compass access denied');
        }
      } catch (err) {
        console.error('Error requesting orientation permission:', err);
        setError(lang === 'hr' 
          ? 'Greška pri pristupanju kompasu' 
          : 'Error accessing compass');
      }
    } else {
      // For Android and older iOS
      setPermissionGranted(true);
      startCompass();
    }
  };

  const startCompass = () => {
    window.addEventListener('deviceorientationabsolute', handleOrientation, true);
    window.addEventListener('deviceorientation', handleOrientation, true);
  };

  const handleOrientation = (event) => {
    let heading = event.alpha;
    
    // Use webkitCompassHeading for iOS
    if (event.webkitCompassHeading !== undefined) {
      heading = event.webkitCompassHeading;
    } else if (event.alpha !== null) {
      // For Android - convert alpha to compass heading
      heading = event.absolute ? event.alpha : 360 - event.alpha;
    }
    
    if (heading !== null) {
      setDeviceHeading(Math.round(heading));
      setIsCalibrated(true);
    }
  };

  // Calculate the angle to rotate the compass needle
  const getCompassRotation = () => {
    if (qiblaDirection === null) return 0;
    // Qibla direction minus device heading gives us where to point
    return qiblaDirection - deviceHeading;
  };

  // Check if pointing towards Qibla (within 5 degrees)
  const isPointingToQibla = () => {
    if (qiblaDirection === null) return false;
    const diff = Math.abs(getCompassRotation());
    const normalizedDiff = diff > 180 ? 360 - diff : diff;
    return normalizedDiff <= 10; // Within 10 degrees
  };

  useEffect(() => {
    getLocation();
    initCompass();

    return () => {
      window.removeEventListener('deviceorientationabsolute', handleOrientation, true);
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  }, []);

  return (
    <Layout user={user}>
      <div className="max-w-2xl mx-auto px-6 py-8 md:py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-4">
            <Compass className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#292524] mb-2">
            {lang === 'hr' ? 'Kibla Kompas' : 'Qibla Compass'}
          </h1>
          <p className="text-[#57534E] font-body">
            {lang === 'hr' 
              ? 'Pronađite smjer Kible za namaz' 
              : 'Find the direction of Qibla for prayer'}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4" />
            <p className="text-[#57534E]">
              {lang === 'hr' ? 'Učitavanje...' : 'Loading...'}
            </p>
          </div>
        )}

        {/* Compass Display */}
        {!loading && qiblaDirection !== null && (
          <div className="flex flex-col items-center">
            {/* Compass Circle */}
            <div 
              className={`relative w-72 h-72 md:w-80 md:h-80 rounded-full border-4 transition-colors duration-300 ${
                isPointingToQibla() 
                  ? 'border-green-500 bg-green-50 shadow-lg shadow-green-200' 
                  : 'border-stone-300 bg-white'
              }`}
            >
              {/* Cardinal Directions */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-full h-full">
                  {/* N, E, S, W markers */}
                  <span className="absolute top-2 left-1/2 -translate-x-1/2 text-lg font-bold text-[#292524]">N</span>
                  <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-lg font-bold text-[#57534E]">S</span>
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-lg font-bold text-[#57534E]">W</span>
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-bold text-[#57534E]">E</span>
                </div>
              </div>

              {/* Compass Rose / Degree Markers */}
              <div className="absolute inset-4 rounded-full border border-stone-200">
                {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => (
                  <div
                    key={deg}
                    className="absolute w-1 h-3 bg-stone-300"
                    style={{
                      left: '50%',
                      top: '0',
                      transformOrigin: '50% calc(50% + ((100% - 6px) / 2))',
                      transform: `translateX(-50%) rotate(${deg}deg)`
                    }}
                  />
                ))}
              </div>

              {/* Qibla Direction Needle */}
              <div 
                ref={compassRef}
                className="absolute inset-0 flex items-center justify-center transition-transform duration-200 ease-out"
                style={{ transform: `rotate(${getCompassRotation()}deg)` }}
              >
                {/* Needle pointing to Qibla */}
                <div className="relative h-full flex flex-col items-center">
                  {/* Kaaba Icon at top */}
                  <div className="absolute top-6">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      isPointingToQibla() ? 'bg-green-500 text-white' : 'bg-primary text-white'
                    }`}>
                      <span className="text-lg">🕋</span>
                    </div>
                  </div>
                  
                  {/* Needle line */}
                  <div className="absolute top-20 w-1 bg-gradient-to-b from-primary to-transparent" style={{ height: 'calc(50% - 40px)' }} />
                </div>
              </div>

              {/* Center dot */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-primary rounded-full shadow-lg" />
            </div>

            {/* Status Message */}
            <div className={`mt-6 px-6 py-3 rounded-xl flex items-center gap-2 transition-colors ${
              isPointingToQibla() 
                ? 'bg-green-100 text-green-700' 
                : 'bg-stone-100 text-[#57534E]'
            }`}>
              {isPointingToQibla() ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">
                    {lang === 'hr' ? 'Usmjereni ste prema Kibli!' : 'You are facing the Qibla!'}
                  </span>
                </>
              ) : (
                <>
                  <Navigation className="w-5 h-5" />
                  <span>
                    {lang === 'hr' ? 'Okrećite se dok ne pronađete Kiblu' : 'Rotate until you find the Qibla'}
                  </span>
                </>
              )}
            </div>

            {/* Qibla Info Card */}
            <div className="mt-8 w-full bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-6 shadow-card">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-stone-50 rounded-xl">
                  <p className="text-sm text-[#57534E] mb-1">
                    {lang === 'hr' ? 'Smjer Kible' : 'Qibla Direction'}
                  </p>
                  <p className="text-2xl font-bold text-primary">
                    {qiblaDirection?.toFixed(1)}°
                  </p>
                </div>
                <div className="text-center p-4 bg-stone-50 rounded-xl">
                  <p className="text-sm text-[#57534E] mb-1">
                    {lang === 'hr' ? 'Vaš smjer' : 'Your Heading'}
                  </p>
                  <p className="text-2xl font-bold text-[#292524]">
                    {deviceHeading}°
                  </p>
                </div>
              </div>

              {location && (
                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-[#57534E]">
                  <MapPin className="w-4 h-4" />
                  <span>
                    {location.latitude.toFixed(4)}°, {location.longitude.toFixed(4)}°
                  </span>
                </div>
              )}
            </div>

            {/* Refresh Button */}
            <button
              onClick={() => {
                getLocation();
                initCompass();
              }}
              className="mt-6 flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              {lang === 'hr' ? 'Osvježi' : 'Refresh'}
            </button>

            {/* Instructions */}
            <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {lang === 'hr' ? 'Upute' : 'Instructions'}
              </h3>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• {lang === 'hr' 
                  ? 'Držite telefon horizontalno' 
                  : 'Hold your phone horizontally'}</li>
                <li>• {lang === 'hr' 
                  ? 'Udalite se od metalnih predmeta' 
                  : 'Move away from metal objects'}</li>
                <li>• {lang === 'hr' 
                  ? 'Kalibrirajte kompas kružnim pokretima' 
                  : 'Calibrate compass with figure-8 motion'}</li>
                <li>• {lang === 'hr' 
                  ? 'Okrenite se dok ikona Kabe nije na vrhu' 
                  : 'Turn until the Kaaba icon is at the top'}</li>
              </ul>
            </div>
          </div>
        )}

        {/* Desktop Fallback */}
        {!loading && qiblaDirection !== null && !isCalibrated && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl text-center">
            <p className="text-blue-700">
              {lang === 'hr' 
                ? 'Za najbolje iskustvo, koristite ovu stranicu na mobilnom uređaju s kompasom.' 
                : 'For the best experience, use this page on a mobile device with a compass.'}
            </p>
            <p className="text-blue-600 mt-2 font-medium">
              {lang === 'hr' 
                ? `Smjer Kible od vaše lokacije: ${qiblaDirection?.toFixed(1)}° od sjeveran` 
                : `Qibla direction from your location: ${qiblaDirection?.toFixed(1)}° from North`}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
